import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, Filter, Users, MapPin, Crown, MessageCircle, UserPlus,
  Globe, Heart, Book, Award, Sparkles, RefreshCw, ChevronDown,
  ChevronUp, X, Star
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { tribalSocialAPI, publicProfileAPI } from '../services/api';
import FollowList from '../components/FollowList';
import FollowButton from '../components/FollowButton';
import PeacePipeMessage from '../components/PeacePipeMessage';
import Header from '../components/Header';
import { useToast } from '../hooks/use-toast';

const ExplorePage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Search and Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    region: '',
    surname: '',
    tribe: '',
    interests: [],
    crestType: '',
    sortBy: 'most_active'
  });
  
  // Data State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Mock data for now - this would be replaced with actual API calls
  const mockUsers = [
    {
      user_id: '1',
      tribal_name: 'Maria Lightfoot',
      profile_picture: 'https://images.unsplash.com/photo-1494790108755-2616b612b642?w=150',
      surnames: ['Lightfoot', 'Running Bear'],
      region: 'Region 1 - Cherokee Territory',
      tribe: 'Cherokee Nation',
      family_crest: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=50',
      bio_preview: 'Preserving Cherokee oral traditions and herbal medicine knowledge...',
      interests: ['Healing', 'Language', 'Ceremonies'],
      match_percentage: 85,
      activity_status: 'Active this week',
      crest_type: 'animal'
    },
    {
      user_id: '2',
      tribal_name: 'Joseph Two Eagles',
      profile_picture: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
      surnames: ['Two Eagles', 'Stands Tall'],
      region: 'Region 5 - Lakota Plains',
      tribe: 'Lakota Sioux',
      family_crest: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=50',
      bio_preview: 'Documenting family genealogy back to the 1800s, passionate about land rights...',
      interests: ['Treaties', 'Land', 'Family History'],
      match_percentage: 78,
      activity_status: 'Active today',
      crest_type: 'geometric'
    },
    {
      user_id: '3',
      tribal_name: 'Sarah Whispering Wind',
      profile_picture: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150',
      surnames: ['Whispering Wind', 'River Song'],
      region: 'Region 12 - Ojibwe Woodlands',
      tribe: 'Ojibwe Nation',
      family_crest: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=50',
      bio_preview: 'Teaching traditional beadwork and maintaining our language through storytelling...',
      interests: ['Language', 'Arts & Crafts', 'Education'],
      match_percentage: 92,
      activity_status: 'Active this month',
      crest_type: 'plant'
    },
    {
      user_id: '4',
      tribal_name: 'Robert Thunder Cloud',
      profile_picture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
      surnames: ['Thunder Cloud', 'Storm Walker'],
      region: 'Region 3 - Navajo Desert',
      tribe: 'Navajo Nation',
      family_crest: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=50',
      bio_preview: 'Traditional weaver and silversmith, connecting with other artisans...',
      interests: ['Arts & Crafts', 'Trading', 'Cultural Preservation'],
      match_percentage: 67,
      activity_status: 'Active yesterday',
      crest_type: 'geometric'
    }
  ];

  const tribalRegions = [
    'Region 1 - Abenaki L\'Nabi',
    'Region 2 - Cherokee',
    'Region 3 - Yamasee',
    'Region 4 - Chickasaw',
    'Region 5 - Kansa (Kaw)',
    'Region 6 - Choctaw',
    'Region 7 - Anasazi',
    'Region 8 - Hopi Zuni',
    'Region 9 - Kānaka Maoli',
    'Region 10 - Arawak (Taíno)',
    'Region 11 - Aztec',
    'Region 12 - Mayan',
    'Region 13 - Wayuu',
    'Region 14 - Goojijwininiwag',
    'Region 15 - Ojibway-Cree',
    'Region 16 - Sioux Tribe',
    'Region 17 - Blackfoot (Niitsítapi)',
    'Region 18 - The Cowichan Tribes',
    'Region 19 - Metis',
    'Region 20 - Haida',
    'Region 21 - Dogrib/Tåîchô',
    'Region 22 - Inuit',
    'Region 23 - Algonquin',
    'Region 24 - Mi\'kmaq'
  ];

  const interestOptions = [
    'Healing', 'Language', 'Treaties', 'Land', 'Ceremonies', 
    'Family History', 'Arts & Crafts', 'Trading', 'Education',
    'Cultural Preservation', 'Oral Traditions', 'Music & Dance'
  ];

  useEffect(() => {
    loadUsers();
  }, [filters, searchTerm, isAuthenticated]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Fetch real profiles from the database
      const profiles = await publicProfileAPI.discoverProfiles({
        limit: 100, // Get all profiles
        skip: 0,
        tribal_affiliation: filters.tribe || undefined,
        region: filters.region || undefined
      });
      
      // Transform profiles to match the expected format
      let transformedUsers = profiles.map(profile => ({
        user_id: profile.user_id,
        tribal_name: profile.tribal_name || profile.full_name,
        profile_picture: profile.profile_picture,
        region: profile.region_flag || 'Unknown Region',
        tribe: profile.known_tribes?.length > 0 ? profile.known_tribes[0].tribe_name : 'Unknown Tribe',
        surnames: profile.surname_heritage?.map(s => s.surname) || [],
        bio_preview: profile.bio_story || '',
        cultural_practices: profile.cultural_practices || [],
        languages_spoken: profile.languages_spoken || [],
        followers_count: profile.followers_count || 0,
        posts_count: profile.posts_count || 0,
        badges: profile.badges || [],
        match_percentage: Math.floor(Math.random() * 40) + 60, // Random match percentage 60-100%
        activity_status: ['Active today', 'Active yesterday', 'Active this week'][Math.floor(Math.random() * 3)],
        interests: profile.cultural_practices || [],
        crest_type: profile.family_crest ? 'Traditional' : 'None',
        family_crest: profile.family_crest
      }));
      
      // Apply additional filters that can't be done at API level
      if (searchTerm) {
        transformedUsers = transformedUsers.filter(user =>
          user.tribal_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.surnames.some(surname => surname.toLowerCase().includes(searchTerm.toLowerCase())) ||
          user.tribe.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.region.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // Apply surname filter
      if (filters.surname) {
        transformedUsers = transformedUsers.filter(user =>
          user.surnames.some(surname => surname.toLowerCase().includes(filters.surname.toLowerCase()))
        );
      }
      
      // Apply interests filter
      if (filters.interests.length > 0) {
        transformedUsers = transformedUsers.filter(user =>
          filters.interests.some(interest => user.interests.includes(interest))
        );
      }
      
      // Apply crest type filter
      if (filters.crestType) {
        transformedUsers = transformedUsers.filter(user => user.crest_type === filters.crestType);
      }
      
      // Apply sorting
      switch (filters.sortBy) {
        case 'most_active':
          transformedUsers.sort((a, b) => {
            const activityOrder = ['Active today', 'Active yesterday', 'Active this week', 'Active this month'];
            return activityOrder.indexOf(a.activity_status) - activityOrder.indexOf(b.activity_status);
          });
          break;
        case 'closest_match':
          transformedUsers.sort((a, b) => b.match_percentage - a.match_percentage);
          break;
        case 'recently_joined':
          transformedUsers.sort((a, b) => new Date(b.created_at || Date.now()) - new Date(a.created_at || Date.now()));
          break;
      }
      
      setUsers(transformedUsers);
      setTotalResults(transformedUsers.length);
      
    } catch (error) {
      console.error('Error loading users:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load community members"
      });
      
      // Fallback to empty array if API fails
      setUsers([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      region: '',
      surname: '',
      tribe: '',
      interests: [],
      crestType: '',
      sortBy: 'most_active'
    });
    setSearchTerm('');
  };

  const toggleInterest = (interest) => {
    setFilters(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const getMatchIcon = (percentage) => {
    if (percentage >= 90) return <Star className="w-4 h-4 text-yellow-500" />;
    if (percentage >= 75) return <Heart className="w-4 h-4 text-red-500" />;
    if (percentage >= 60) return <Users className="w-4 h-4 text-blue-500" />;
    return <Globe className="w-4 h-4 text-gray-500" />;
  };

  // Removed authentication requirement to allow public community discovery

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              🧭 Explore Community
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover relatives, connect with similar regions, and build your tribal network. 
              Find people who share your heritage, interests, and ancestral connections.
            </p>
          </div>

          {/* Search Bar */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-center">
            <div className="relative flex-1 max-w-2xl">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search People, Tribes, Surnames, or Regions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 text-lg"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </div>

        {/* Filter Sidebar */}
        {showFilters && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>🧬 Advanced Filters</span>
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Region Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🌐 Region (24 Tribal Zones)
                  </label>
                  <Select value={filters.region || 'all-regions'} onValueChange={(value) => setFilters(prev => ({...prev, region: value === 'all-regions' ? '' : value}))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select region..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all-regions">All Regions</SelectItem>
                      {tribalRegions.map((region) => (
                        <SelectItem key={region} value={region}>{region}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Surname Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🧬 Surname
                  </label>
                  <Input
                    placeholder="Enter surname..."
                    value={filters.surname}
                    onChange={(e) => setFilters(prev => ({...prev, surname: e.target.value}))}
                  />
                </div>

                {/* Tribe Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🏕️ Tribe/Nation
                  </label>
                  <Input
                    placeholder="Enter tribe name..."
                    value={filters.tribe}
                    onChange={(e) => setFilters(prev => ({...prev, tribe: e.target.value}))}
                  />
                </div>

                {/* Interests Filter */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🎓 Shared Interests
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {interestOptions.map((interest) => (
                      <Button
                        key={interest}
                        variant={filters.interests.includes(interest) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleInterest(interest)}
                      >
                        {interest}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Crest Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📜 Crest Type
                  </label>
                  <Select value={filters.crestType || 'all-types'} onValueChange={(value) => setFilters(prev => ({...prev, crestType: value === 'all-types' ? '' : value}))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Any crest type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all-types">All Types</SelectItem>
                      <SelectItem value="animal">Animal</SelectItem>
                      <SelectItem value="plant">Plant</SelectItem>
                      <SelectItem value="geometric">Geometric</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Sort By */}
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort By
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'most_active', label: 'Most Active' },
                      { value: 'closest_match', label: 'Closest Match' },
                      { value: 'recently_joined', label: 'Recently Joined' }
                    ].map((option) => (
                      <Button
                        key={option.value}
                        variant={filters.sortBy === option.value ? "default" : "outline"}
                        size="sm"
                        onClick={() => setFilters(prev => ({...prev, sortBy: option.value}))}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900">
              🧑🏽‍🤝‍🧑🏾 Community Members
            </h2>
            <Badge variant="secondary">
              {totalResults} {totalResults === 1 ? 'result' : 'results'}
            </Badge>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={loadUsers}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* User Cards Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-full"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : users.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No members found</h3>
              <p className="text-gray-500 mb-4">
                Try adjusting your search terms or filters to find more community members.
              </p>
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {users.map((user) => (
              <Card key={user.user_id} className="group hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  {/* User Header - Clickable */}
                  <div 
                    className="flex items-center space-x-4 mb-4 cursor-pointer hover:bg-gray-50 -m-2 p-2 rounded-lg transition-colors"
                    onClick={() => navigate(`/profile/${user.user_id}`)}
                  >
                    <div className="relative">
                      <Avatar className="w-16 h-16">
                        <AvatarImage src={user.profile_picture} />
                        <AvatarFallback>
                          {user.tribal_name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {user.family_crest && (
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full p-0.5 shadow-sm">
                          <img 
                            src={user.family_crest} 
                            alt="Family crest" 
                            className="w-full h-full rounded-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate hover:text-blue-600 transition-colors">
                        {user.tribal_name}
                      </h3>
                      <div className="flex items-center space-x-1 mt-1">
                        {getMatchIcon(user.match_percentage)}
                        <span className="text-sm text-gray-500">
                          {user.match_percentage}% match
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Surnames - Clickable for deeper search */}
                  <div className="mb-3">
                    <p className="text-sm text-gray-600 mb-1">Surnames:</p>
                    <div className="flex flex-wrap gap-1">
                      {user.surnames.map((surname, index) => (
                        <Badge 
                          key={index} 
                          variant="outline" 
                          className="text-xs cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card click
                            setFilters(prev => ({...prev, surname: surname}));
                            setSearchTerm('');
                          }}
                        >
                          {surname}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Region & Tribe */}
                  <div className="mb-3">
                    <div className="flex items-center text-sm text-gray-600 mb-1">
                      <MapPin className="w-3 h-3 mr-1" />
                      <span className="truncate">{user.region}</span>
                    </div>
                    <div className="text-sm font-medium text-gray-800">
                      {user.tribe}
                    </div>
                  </div>

                  {/* Click hint */}
                  <div 
                    className="text-xs text-blue-600 mb-3 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:text-blue-800"
                    onClick={() => navigate(`/profile/${user.user_id}`)}
                  >
                    Click to view full profile →
                  </div>

                  {/* Bio Preview - Also clickable */}
                  <p 
                    className="text-sm text-gray-600 mb-3 line-clamp-2 cursor-pointer hover:text-gray-800 transition-colors"
                    onClick={() => navigate(`/profile/${user.user_id}`)}
                  >
                    {user.bio_preview}
                  </p>

                  {/* Interests */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1">
                      {user.interests.slice(0, 3).map((interest, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {interest}
                        </Badge>
                      ))}
                      {user.interests.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{user.interests.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Activity Status */}
                  <div className="text-xs text-gray-500 mb-4">
                    {user.activity_status}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between">
                    <FollowButton
                      userId={user.user_id}
                      size="sm"
                      variant="default"
                    />
                    
                    <PeacePipeMessage
                      receiverId={user.user_id}
                      receiverName={user.tribal_name}
                      trigger={
                        <Button variant="outline" size="sm">
                          <MessageCircle className="w-4 h-4" />
                        </Button>
                      }
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExplorePage;